<script setup>
// Lead Workspace — Site Visit tab. Fully backed by the existing
// Nexceo::SiteVisit model + SiteVisitsController (already lead_id-scoped) —
// same data/actions as the Calendar/Kanban site-visit UI, just presented
// per-lead. No pre-visit checklist or attendee-count fields exist on the
// model today, so those reference sections are intentionally omitted rather
// than faked; only the real `notes` free-text field is shown.
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import siteVisitsAPI from 'dashboard/api/nexceo/siteVisits';
import SiteVisitForm from '../../calendar/SiteVisitForm.vue';
import { formatDate, formatDateTime, humanize } from '../leadFormatters';

const router = useRouter();
const { accountId } = useAccount();

const props = defineProps({
  lead: { type: Object, required: true },
});
const emit = defineEmits(['reload']);

const visits = ref([]);
const isLoading = ref(true);
const isFormOpen = ref(false);
const editingVisit = ref(null);

async function load() {
  isLoading.value = true;
  try {
    const { data } = await siteVisitsAPI.get({ lead_id: props.lead.id });
    const list = Array.isArray(data) ? data : (data?.site_visits ?? data?.payload ?? []);
    visits.value = list.sort(
      (a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)
    );
  } catch {
    visits.value = [];
  } finally {
    isLoading.value = false;
  }
}
onMounted(load);

const STATUS_META = {
  scheduled: { label: 'Scheduled', cls: 'bg-woot-100 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400' },
  confirmed: { label: 'Confirmed', cls: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400' },
  rescheduled: { label: 'Rescheduled', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-400' },
  completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400' },
  no_show: { label: 'No Show', cls: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400' },
};

const upcomingVisit = computed(() =>
  visits.value.find(v => ['scheduled', 'confirmed', 'rescheduled'].includes(v.status))
);

function openSchedule() {
  editingVisit.value = null;
  isFormOpen.value = true;
}

function openReschedule(visit) {
  editingVisit.value = visit;
  isFormOpen.value = true;
}

function onSaved() {
  isFormOpen.value = false;
  load();
  emit('reload');
}

async function runAction(action, visit) {
  try {
    if (action === 'confirm') await siteVisitsAPI.confirm(visit.id);
    if (action === 'complete') await siteVisitsAPI.complete(visit.id);
    if (action === 'cancel') await siteVisitsAPI.cancel(visit.id, 'Cancelled from Lead Workspace');
    if (action === 'resend') await siteVisitsAPI.resendConfirmation(visit.id);
    if (action === 'retry_meet') await siteVisitsAPI.retryMeetLink(visit.id);
    useAlert('Site visit updated.');
    load();
    emit('reload');
  } catch {
    useAlert('Action failed.');
  }
}

const notesDraft = ref('');
const isSavingNotes = ref(false);

function syncNotesDraft() {
  notesDraft.value = upcomingVisit.value?.notes || '';
}

async function saveNotes() {
  if (!upcomingVisit.value) return;
  isSavingNotes.value = true;
  try {
    await siteVisitsAPI.update(upcomingVisit.value.id, { site_visit: { notes: notesDraft.value } });
    useAlert('Notes saved.');
    load();
  } catch {
    useAlert('Failed to save notes.');
  } finally {
    isSavingNotes.value = false;
  }
}

const mapsHref = computed(() =>
  upcomingVisit.value?.location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(upcomingVisit.value.location)}`
    : null
);

const TIMELINE_STEPS = ['scheduled', 'confirmed', 'completed'];
function stepDone(step) {
  if (!upcomingVisit.value) return false;
  const order = TIMELINE_STEPS.indexOf(step);
  const current = TIMELINE_STEPS.indexOf(upcomingVisit.value.status);
  return current >= 0 && order <= current;
}

function viewProject() {
  if (!upcomingVisit.value?.project) return;
  router.push({
    name: 'nexceo_sales_project_show',
    params: { accountId: accountId.value, id: upcomingVisit.value.project.id, tab: 'overview' },
  });
}

watch(upcomingVisit, syncNotesDraft, { immediate: true });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="p-4 space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">Site Visits</h3>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg hover:bg-woot-600 font-medium"
        @click="openSchedule"
      >
        <span class="i-lucide-plus size-3.5" /> Schedule Visit
      </button>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-8">Loading…</div>

    <template v-else>
      <!-- Next visit strip -->
      <div v-if="upcomingVisit" class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div class="rounded-lg border border-n-weak p-3">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">Next Visit</p>
          <p class="text-sm font-medium text-n-slate-12">{{ formatDateTime(upcomingVisit.scheduled_at) }}</p>
        </div>
        <div class="rounded-lg border border-n-weak p-3">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">Visit Status</p>
          <span
            class="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5"
            :class="STATUS_META[upcomingVisit.status]?.cls"
          >
            {{ STATUS_META[upcomingVisit.status]?.label || humanize(upcomingVisit.status) }}
          </span>
        </div>
        <div class="rounded-lg border border-n-weak p-3">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">Visit Type</p>
          <p class="text-sm font-medium text-n-slate-12 capitalize">{{ humanize(upcomingVisit.visit_type) }}</p>
        </div>
        <div class="rounded-lg border border-n-weak p-3">
          <p class="text-[10px] uppercase tracking-wide text-n-slate-8">Location</p>
          <p class="text-sm font-medium text-n-slate-12 truncate">{{ upcomingVisit.location || '—' }}</p>
        </div>
      </div>

      <!-- Action bar for upcoming visit -->
      <div v-if="upcomingVisit" class="flex flex-wrap items-center gap-2">
        <button
          v-if="upcomingVisit.status === 'scheduled'"
          class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak hover:border-woot-400"
          @click="runAction('confirm', upcomingVisit)"
        >
          Confirm
        </button>
        <button
          class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak hover:border-woot-400"
          @click="openReschedule(upcomingVisit)"
        >
          Reschedule
        </button>
        <button
          class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak hover:border-woot-400"
          @click="runAction('resend', upcomingVisit)"
        >
          Resend Confirmation
        </button>
        <button
          v-if="upcomingVisit.meeting_provider"
          class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak hover:border-woot-400"
          @click="runAction('retry_meet', upcomingVisit)"
        >
          Retry Meet Link
        </button>
        <button
          class="text-xs px-2.5 py-1.5 rounded-lg bg-green-500 text-white hover:bg-green-600"
          @click="runAction('complete', upcomingVisit)"
        >
          Mark Completed
        </button>
        <button
          class="text-xs px-2.5 py-1.5 rounded-lg text-red-600 border border-red-200 hover:bg-red-50"
          @click="runAction('cancel', upcomingVisit)"
        >
          Cancel
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <!-- Site Visit Plan -->
        <div v-if="upcomingVisit" class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">Site Visit Plan</h3>
            <button class="text-xs text-woot-600 hover:underline" @click="openReschedule(upcomingVisit)">
              Edit
            </button>
          </div>
          <dl class="space-y-2.5 text-sm">
            <div>
              <dt class="text-xs text-n-slate-8">Date & Time</dt>
              <dd class="text-n-slate-12">{{ formatDateTime(upcomingVisit.scheduled_at) }}</dd>
            </div>
            <div>
              <dt class="text-xs text-n-slate-8">Project</dt>
              <dd class="text-n-slate-12">{{ upcomingVisit.project?.name || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-n-slate-8">Meeting Point</dt>
              <dd class="text-n-slate-12">{{ upcomingVisit.location || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-n-slate-8">Assigned To</dt>
              <dd class="text-n-slate-12">{{ upcomingVisit.assigned_agent?.name || 'Unassigned' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-n-slate-8">Created On</dt>
              <dd class="text-n-slate-12">{{ formatDate(upcomingVisit.created_at) }}</dd>
            </div>
          </dl>
          <a
            v-if="mapsHref"
            :href="mapsHref"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-3 flex items-center justify-center gap-1.5 w-full py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-woot-400"
          >
            <span class="i-lucide-map-pin size-3.5" /> Open in Maps
          </a>
        </div>

        <!-- Property to Show + Customer Notes -->
        <div class="flex flex-col gap-4">
          <div v-if="upcomingVisit?.project" class="rounded-xl border border-n-weak bg-n-background p-4">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Property to Show</h3>
            <div class="flex items-center justify-between rounded-lg border border-n-weak p-2.5">
              <span class="text-sm text-n-slate-12">{{ upcomingVisit.project.name }}</span>
              <button class="text-xs text-woot-600 hover:underline shrink-0" @click="viewProject">
                View Details
              </button>
            </div>
          </div>

          <div v-if="upcomingVisit" class="rounded-xl border border-n-weak bg-n-background p-4">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Customer Notes for Visit</h3>
            <textarea
              v-model="notesDraft"
              rows="4"
              placeholder="Add notes for this visit…"
              class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500 resize-none"
            />
            <button
              :disabled="isSavingNotes"
              class="mt-2 text-xs px-3 py-1.5 rounded-lg bg-woot-500 text-white disabled:opacity-50"
              @click="saveNotes"
            >
              {{ isSavingNotes ? 'Saving…' : 'Save Notes' }}
            </button>
          </div>
        </div>

        <!-- Visit Timeline -->
        <div class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Visit Timeline</h3>

          <div v-if="upcomingVisit" class="relative pl-5 border-l border-n-weak space-y-3 mb-4">
            <div v-for="step in TIMELINE_STEPS" :key="step" class="relative">
              <span
                class="absolute -left-[21px] top-0.5 size-2.5 rounded-full ring-2 ring-n-background"
                :class="stepDone(step) ? 'bg-green-500' : 'bg-n-alpha-3'"
              />
              <p class="text-xs font-medium capitalize" :class="stepDone(step) ? 'text-n-slate-12' : 'text-n-slate-8'">
                {{ humanize(step) }}
              </p>
            </div>
          </div>

          <p v-if="!visits.length" class="text-sm text-n-slate-8">No site visits scheduled for this lead yet.</p>
          <ul v-else class="space-y-3 pt-3 border-t border-n-weak">
            <li
              v-for="v in visits"
              :key="v.id"
              class="flex items-center justify-between gap-3 pb-3 border-b border-n-weak last:border-0 last:pb-0"
            >
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12">{{ formatDateTime(v.scheduled_at) }}</p>
                <p class="text-xs text-n-slate-8 capitalize">{{ humanize(v.visit_type) }} · {{ v.location || 'No location set' }}</p>
              </div>
              <span
                class="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
                :class="STATUS_META[v.status]?.cls"
              >
                {{ STATUS_META[v.status]?.label || humanize(v.status) }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <SiteVisitForm
      :is-open="isFormOpen"
      :prefill-lead="lead"
      :edit-visit="editingVisit"
      @close="isFormOpen = false"
      @saved="onSaved"
    />
  </div>
</template>
